// React i hooks
import { memo, useCallback, useEffect, useState } from "react";

// Helpers
import { classNames, useTranslate } from "helpers";
import { useHandleDropdownChange } from "services/forms/hooks";

// API i konstante
import { FleetVehicleSearchFormValues } from "services/api/fleet/vehicles/types";
import { initialFleetVehicleSearchForm } from "services/api/fleet/vehicles/consts";

// UI komponente
import { Button } from "ui/components/Button/Button";
import Dropdown from "ui/components/Dropdown/Dropdown/Dropdown";

// Dropdown komponente
import VehicleManufacturerDropdown from "ui/components/Dropdown/VehicleManufacturerDropdown/VehicleManufacturerDropdown";
import VehicleModelDropdown from "ui/components/Dropdown/VehicleModelDropdown/VehicleModelDropdown";
import VehicleTypeDropdown from "ui/components/Dropdown/VehicleTypeDropdown/VehicleTypeDropdown";

// Tipovi
import { ListingSearchFilterProps } from "./types";
import VehicleSearchFiltersModal from "ui/modules/fleet/vehicles/pages/VehicleSearchPage/components/VehicleSearchFilters/VehicleSearchFiltersModal";
import { Chip } from "ui/components/Chip";
import { TableViewToggle } from "ui/components/Table/TableViewToggle";
import { LISTING_STATUSES } from "ui/modules/sales/pages/listings/consts";

// Isti pattern kao u `VehicleModelDropdown` (label lijevo, sekundarni info
// desno u text-xs slate-400). `flex items-center` drži cijelo na jednoj liniji,
// pa visina dropdowna ostaje ista kao kod ostalih u toolbaru.
const renderOption = (label: string, description: string, dotClass?: string) => (
  <div className="flex justify-between items-center w-full gap-3">
    <span className="inline-flex items-center gap-2">
      {dotClass && <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${dotClass}`} />}
      {label}
    </span>
    <span className="text-slate-400 text-xs">{description}</span>
  </div>
);

// Nazivi i boje dolaze iz LISTING_STATUSES, isti kao u tablici i na karticama.
const PUBLISHED_FILTER_OPTIONS = LISTING_STATUSES.map((status) => ({
  value: status.code,
  text: renderOption(status.label, status.description, status.dotClassName),
  displayName: status.label,
}));

const ListingSearchFilters: React.FC<ListingSearchFilterProps> = (props) => {
  const { onSearchChange, viewType, onViewTypeChange, publishedFilter, onPublishedFilterChange } = props;

  const { t } = useTranslate();

  const [formValues, setFormValues] = useState<FleetVehicleSearchFormValues>(
    initialFleetVehicleSearchForm
  );
  const [showModal, setShowModal] = useState<boolean>(false);

  useEffect(() => {
    setFormValues((old) => ({
      ...old,
      vehicleModelId: "",
      vehicleTypeId: "",
      advancedSearch: old.advancedSearch.filter(
        (item) => !["vehicleModelId", "vehicleTypeId"].includes(item.name)
      ),
    }));
  }, [formValues.vehicleManufacturerId]);

  useEffect(() => {
    setFormValues((old) => ({
      ...old,
      vehicleTypeId: "",
      advancedSearch: old.advancedSearch.filter(
        (item) => item.name !== "vehicleTypeId"
      ),
    }));
  }, [formValues.vehicleModelId]);

  const handleAdditionalFilterClick = useCallback(
    (formValues: FleetVehicleSearchFormValues) => {
      setFormValues((prevValues) => ({
        ...prevValues,
        ...formValues,
      }));
      onSearchChange(formValues);
      setShowModal(false);
    },
    [setFormValues, setShowModal]
  );

  const handleResetFormClick = useCallback(() => {
    setFormValues(initialFleetVehicleSearchForm);
    onPublishedFilterChange(null);
  }, []);

  const handleRemoveFilterClick = useCallback(
    (id: string, name: string, text: string) => {
      setFormValues((prevValues) => {
        let fieldsToRemove: string[] = [name];
        if (name === "vehicleManufacturerId") {
          fieldsToRemove = ["vehicleManufacturerId", "vehicleModelId", "vehicleTypeId"];
        } else if (name === "vehicleModelId") {
          fieldsToRemove = ["vehicleModelId", "vehicleTypeId"];
        }

        const updatedValues = {
          ...prevValues,
          advancedSearch: prevValues.advancedSearch.filter(
            (item) => !fieldsToRemove.includes(item.name)
          ),
        };

        fieldsToRemove.forEach((field) => {
          (updatedValues as any)[field] = null;
        });

        return updatedValues;
      });
      onSearchChange(formValues);
    },
    [setFormValues]
  );

  const handleDropdownChange = useHandleDropdownChange(
    setFormValues,
    formValues,
    formValues.advancedSearch
  );

  const handlePublishedFilterChange = useCallback(
    (_e: any, _name: string, item: any) => {
      onPublishedFilterChange(item.value || null);
    },
    [onPublishedFilterChange]
  );

  useEffect(() => {
    onSearchChange(formValues);
  }, [formValues.vehicleManufacturerId, formValues.vehicleModelId, formValues.vehicleTypeId]);

  return (
    <>
      <div className="overflow-x-scroll md:overflow-x-visible scrollbar-hide px-4 xl:px-0">
        <div className="flex justify-between items-center mb-3">
          <div className="flex flex-wrap gap-3 items-center">
            <div className="hidden gap-3 xl:flex">
              <div className="min-w-[200px]">
                <VehicleManufacturerDropdown
                  placeholder={t("admin.vehicle.manufacturer.placeholder")}
                  name="vehicleManufacturerId"
                  value={formValues.vehicleManufacturerId}
                  onChange={handleDropdownChange}
                  width="full"
                />
              </div>
              <div className="min-w-[200px]">
                <VehicleModelDropdown
                  placeholder={t("admin.vehicle.model.placeholder")}
                  name="vehicleModelId"
                  vehicleManufacturerId={formValues.vehicleManufacturerId}
                  value={formValues.vehicleModelId}
                  onChange={handleDropdownChange}
                  width="full"
                />
              </div>
              <div className="min-w-[200px]">
                <VehicleTypeDropdown
                  placeholder={t("admin.vehicle.type.placeholder")}
                  name="vehicleTypeId"
                  vehicleManufacturerId={formValues.vehicleManufacturerId}
                  vehicleModelId={formValues.vehicleModelId}
                  value={formValues.vehicleTypeId}
                  onChange={handleDropdownChange}
                />
              </div>
              <div className="min-w-[500px]">
                <Dropdown
                  name="publishedFilter"
                  placeholder={t("admin.sales.listing.search.publishedfilter.placeholder")}
                  options={PUBLISHED_FILTER_OPTIONS}
                  value={publishedFilter ?? ""}
                  onChange={handlePublishedFilterChange}
                  width="full"
                
                />
              </div>
            </div>
            <Button
              variant="outlined"
              startIcon="/media/icons/text-search.svg"
              onClick={() => setShowModal(true)}
            />
            <div className="mr-3 md:mr-6 lg:md-12 flex items-center">
              <span
                onClick={handleResetFormClick}
                className="text-xs font-bold cursor-pointer text-gray-700"
              >
                {t("admin.reset")}
              </span>
            </div>
          </div>
          <div className="flex items-center justify-center">
            <div className="hidden items-center gap-4 text-xs text-slate-500 mr-6 md:flex">
              {LISTING_STATUSES.map((status) => (
                <span key={status.code} className="inline-flex items-center gap-1.5">
                  <span className={classNames("w-1.5 h-1.5 rounded-full", status.dotClassName)} />
                  {status.label}
                </span>
              ))}
            </div>
            <TableViewToggle
              value={viewType}
              onChange={onViewTypeChange}
              options={[
                { value: "table", label: t("admin.table.viewtoggle.table"), icon: "/media/icons/table.svg" },
                { value: "grid", label: t("admin.table.viewtoggle.grid"), icon: "/media/icons/grid.svg" },
              ]}
            />
          </div>
        </div>
        <div className="mb-3 flex gap-1">
          {formValues.advancedSearch &&
            formValues.advancedSearch.map((item, index: number) => {
              return (
                <Chip
                  name={item.name}
                  id={item.value}
                  text={`${item.label ? `${item.label}:` : ""}${item.text}`}
                  key={index}
                  onClick={handleRemoveFilterClick}
                />
              );
            })}
        </div>
      </div>
      <VehicleSearchFiltersModal
        show={showModal}
        onSuccess={handleAdditionalFilterClick}
        onClose={() => setShowModal(false)}
        formValues={formValues}
        setFormValues={setFormValues}
      />
    </>
  );
};
export default memo(ListingSearchFilters);
